import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/react-hooks";
import { Box, Button, Card, Collapse, Typography } from "@mui/material";
import {
  MyHrUsersImportCollapseLabel,
  MyHrUsersImportDialog,
} from "my-hr/components";
import { OcFormSwitch } from "oc/components";
import { OC_UPDATE_PROPERTY } from "oc/graphql";
import { useOcClientState, useOcProperty } from "oc/hooks";

export const MyHrUsersImportSSHRCard: React.FC = () => {
  const { t } = useTranslation();

  // Mutation for updating import type property
  const [updateProperty] = useMutation<
    { updateProperty: { success: boolean } },
    { resource: string; key: string; value: string }
  >(OC_UPDATE_PROPERTY);

  // Dialog state
  const [showDialog, setShowDialog] = useOcClientState<boolean>(
    "showSSHRUsersImportDialog",
    false,
  );

  // Import lock state from properties
  const { data: importLockedRaw } = useOcProperty({
    resource: "system",
    key: "importLocked",
    pollInterval: showDialog ? 1000 : 0,
  });
  const importLocked = importLockedRaw !== "false";

  // Collapse state for the import section
  const [showImportCollapse, setShowImportCollapse] = useOcClientState<boolean>(
    "showImportSSHRUsersRoleCollapse",
    false,
  );

  // Login type property
  const { data: loginType } = useOcProperty({
    resource: "system",
    key: "loginType",
  });

  // Automatic password generation toggle
  const [autoPasswordGen, setAutoPasswordGen] = useState<boolean>(false);

  const handleToggleDialog = (): void => {
    setShowDialog(!showDialog);
  };

  const handleExpandClick = (): void => {
    setShowImportCollapse(!showImportCollapse);
  };

  const handleCloseDialog = (): void => {
    setShowDialog(false);
  };

  const handleFinishImport = (): void => {
    updateProperty({
      variables: {
        resource: "system",
        key: "importType",
        value: "jdolber",
      },
    });
    setShowDialog(false);
  };

  return (
    <div>
      <MyHrUsersImportDialog
        onShowDialog={handleToggleDialog}
        showDialog={showDialog}
        finishImport={handleFinishImport}
        importType="sshr"
        onCloseDialog={handleCloseDialog}
        automaticPasswordGeneration={autoPasswordGen}
        roleFilters={[]} // Provide an empty array or appropriate value for roleFilters
      />

      <Card elevation={0}>
        <MyHrUsersImportCollapseLabel
          expanded={showImportCollapse}
          handleExpandClick={handleExpandClick}
          label={t("MY_HR_IMPORT_USERS_FROM_SSHR")}
        />

        <Collapse
          id="my-hr-users-import-card"
          in={showImportCollapse}
          timeout="auto"
          unmountOnExit
        >
          <Box px={2}>
            <Box sx={{ borderTop: "2px solid #C6CACC" }} />

            {loginType === "legacy" && (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                pt={4}
                pb={1.5}
              >
                <Box display="flex" alignItems="center" pb={1.5}>
                  <OcFormSwitch
                    id="automaticPasswordGeneration"
                    checked={autoPasswordGen}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setAutoPasswordGen(e.target.checked)
                    }
                    disabled={false}
                    inputProps={{ "aria-label": "controlled" }}
                  />
                  <Box width={2} />
                  <Typography variant="body1">
                    {t("MY_HR_AUTOMATIC_PASSWORD_GENERATION")}
                  </Typography>
                </Box>
                <Typography variant="caption">
                  {t("MY_HR_AUTOMATIC_PASSWORD_GENERATION_INFO")}
                </Typography>
              </Box>
            )}

            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              py={4.75}
              sx={{ position: "relative" }}
            >
              <Button
                id="my-hr-users-import-button"
                variant="contained"
                color="primary"
                disableElevation
                onClick={() => setShowDialog(true)}
                disabled={importLocked}
              >
                {t("MY_HR_IMPORT_USERS")}
              </Button>
            </Box>

            <Typography variant="caption" align="center">
              {importLocked
                ? t("MY_HR_IMPORT_SSHR_USERS_LOCKED_INFO")
                : t("MY_HR_IMPORT_SSHR_USERS_TEXT_INFO")}
            </Typography>

            <Box height={4} />
          </Box>
        </Collapse>
      </Card>
    </div>
  );
};

export default MyHrUsersImportSSHRCard;
